import { fetchWithRetry } from './retry'

/**
 * The production Convex deployment URL. Convex injects `CONVEX_CLOUD_URL`
 * into every Convex runtime, and that value is the only production signal
 * there: prod sets neither `NEXT_PUBLIC_APP_URL` nor `BREW_DEPLOYMENT_MODE`,
 * and `NODE_ENV` is absent in the Convex runtime. A Next.js server action
 * does not receive `CONVEX_CLOUD_URL` — production there is `VERCEL_ENV`
 * (`isProductionCallbackDeployment`). Re-exported by
 * `convex/shared/internal_callback_origin.ts`, which must keep keying on the
 * cloud URL alone.
 */
export const PROD_CONVEX_CLOUD_URL = 'https://focused-dogfish-83.convex.cloud'

export const PROD_APP_ORIGIN = 'https://brew.new'
const PRODUCTION_APP_HOST = 'brew.new'

/** Lowercased host without a leading `www.` or a trailing DNS root dot. */
function callbackHost(origin: string | URL): string | null {
  try {
    return new URL(origin).hostname
      .toLowerCase()
      .replace(/\.$/, '')
      .replace(/^www\./, '')
  } catch {
    return null
  }
}

type CallbackEnv = Readonly<Record<string, string | undefined>>

/**
 * Production for this callback family.
 *
 * Inside Convex, `CONVEX_CLOUD_URL` is injected and is the only signal. A
 * leaked `VERCEL_ENV=production` on a preview backend must not win, or the
 * preview would post brew.new again.
 *
 * A Next.js server action does not get `CONVEX_CLOUD_URL`. Production there
 * is `VERCEL_ENV=production` — the Slack link handoff runs in that runtime.
 */
function isProductionCallbackDeployment(env: CallbackEnv): boolean {
  const convexCloudUrl = env.CONVEX_CLOUD_URL?.trim()
  if (convexCloudUrl) {
    return convexCloudUrl === PROD_CONVEX_CLOUD_URL
  }
  return env.VERCEL_ENV?.trim().toLowerCase() === 'production'
}

/**
 * Headers for one Convex -> app `/api/internal/*` callback to `origin`.
 *
 * Preview web apps sit behind Vercel Deployment Protection, which answers a
 * server-to-server POST with a 302 to `vercel.com/sso-api` BEFORE the route's
 * own `Authorization: Bearer` check runs. A preview Convex deployment can hold
 * a correct app origin and still never reach it: every callback bounced at the
 * edge. `VERCEL_AUTOMATION_BYPASS_SECRET` is Vercel's sanctioned key for this,
 * and the preview build syncs it onto the deployment
 * (`scripts/sync-preview-convex-env.mjs`).
 *
 * Never sent to production: prod has no deployment protection, and the header
 * would hand the project-wide bypass secret to a host that does not need it.
 * One implementation for BOTH callback families — `postInternalAppJson` here and
 * the `resolveInternalCallbackOrigin` sites under `convex/`.
 */
export function internalCallbackHeaders(
  bearer: string,
  origin: string | URL,
  env: CallbackEnv = process.env
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${bearer}`,
    'Content-Type': 'application/json',
  }
  const bypass = env.VERCEL_AUTOMATION_BYPASS_SECRET?.trim()
  if (bypass && callbackHost(origin) !== PRODUCTION_APP_HOST) {
    headers['x-vercel-protection-bypass'] = bypass
  }
  return headers
}

/**
 * Origin for the `postInternalAppJson` callback family (insight emails,
 * comment-mention mail), or `null` when this deployment has no safe target.
 *
 * Convex actions cannot import the Next `server-only` origin helper, so this
 * mirrors `resolveInternalCallbackOrigin` in `convex/shared/`:
 *
 *   1. An explicit `INTERNAL_APP_ORIGIN`, then `NEXT_PUBLIC_APP_URL`, wins —
 *      EXCEPT a non-production deployment naming the production origin.
 *   2. The production deployment falls back to the production origin, which
 *      is how prod resolves (it sets neither var). On Vercel, production is
 *      `VERCEL_ENV` when `CONVEX_CLOUD_URL` was not injected.
 *   3. Every other deployment gets `null`: nowhere safe to call.
 *
 * This family used to fall back to `https://brew.new` UNCONDITIONALLY. So a
 * preview whose origin was unset — a fresh preview, or one whose build-time
 * sync did not land — posted its insight and comment-mention callbacks to
 * PRODUCTION, the same fault that sent ~115k junk requests/day at prod from
 * the other callback sites before they were guarded. Withholding the bypass
 * header did not stop the request; only refusing to dispatch does.
 */
export function resolveInternalCallbackAppUrl(
  env: CallbackEnv = process.env
): string | null {
  const isProductionDeployment = isProductionCallbackDeployment(env)
  for (const candidate of [env.INTERNAL_APP_ORIGIN, env.NEXT_PUBLIC_APP_URL]) {
    const trimmed = candidate?.trim()
    if (trimmed) {
      if (
        !isProductionDeployment &&
        callbackHost(trimmed) === PRODUCTION_APP_HOST
      ) {
        return null
      }
      return trimmed
    }
  }
  return isProductionDeployment ? PROD_APP_ORIGIN : null
}

export async function postInternalAppJson(args: {
  pathname: string
  body: unknown
  label: string
  attempts?: number
  timeoutMs?: number
}): Promise<Response> {
  const secret = process.env.INTERNAL_API_SECRET?.trim()
  if (!secret) {
    throw new Error('INTERNAL_API_SECRET is not configured')
  }
  const origin = resolveInternalCallbackAppUrl()
  if (!origin) {
    // Same contract as the resolveInternalCallbackOrigin sites: refuse to
    // dispatch rather than call another environment.
    throw new Error(
      'no internal callback origin — point NEXT_PUBLIC_APP_URL at the web app paired with this deployment (the production origin is refused from a non-production deployment)'
    )
  }
  return await fetchWithRetry(
    new URL(args.pathname, origin),
    {
      method: 'POST',
      headers: internalCallbackHeaders(secret, origin),
      body: JSON.stringify(args.body),
    },
    {
      label: args.label,
      operation: 'idempotent_write',
      attempts: args.attempts ?? 3,
      timeoutMs: args.timeoutMs ?? 30_000,
    }
  )
}
